import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import { CheckCircleFilled, ArrowRightOutlined, ArrowLeftOutlined, CloseOutlined } from '@ant-design/icons';

// ─── localStorage keys ────────────────────────────────────────────────────────

const LS_KEYS = {
    ads:     'dismiss_step_ads_api',
    expense: 'dismiss_step_expense',
    cogs:    'dismiss_step_cogs_tax',
};

const readDismissed = () => ({
    ads:     localStorage.getItem(LS_KEYS.ads)     === 'true',
    expense: localStorage.getItem(LS_KEYS.expense) === 'true',
    cogs:    localStorage.getItem(LS_KEYS.cogs)    === 'true',
});

// ─── Step definitions (static) ───────────────────────────────────────────────

const STEPS = [
    {
        id:          1,
        dismissKey:  null,          // mandatory — cannot be dismissed
        title:       'Connect SP API',
        label:       'Step 01',
        icon:        '🔗',
        description: 'Link your Amazon Selling Partner account. This is required before any other setup can proceed.',
        ctaLabel:    'Connect SP API',
        ctaLink:     '/settings/marketplace-credentials?tab=spApi&creds=true',
        mandatory:   true,
    },
    {
        id:          2,
        dismissKey:  'ads',
        title:       'Connect Ads API',
        label:       'Step 02',
        icon:        '📢',
        description: 'Link your Amazon Ads account to track PPC performance and ad spend across your campaigns.',
        ctaLabel:    'Connect Ads API',
        ctaLink:     '/settings/marketplace-credentials?tab=advertising&creds=true',
        mandatory:   false,
    },
    {
        id:          3,
        dismissKey:  'expense',
        title:       'Add Expense Data',
        label:       'Step 03',
        icon:        '💰',
        description: 'Add your business expenses so your profit reports accurately reflect all operational costs.',
        ctaLabel:    'Add Expenses',
        ctaLink:     '/settings/expenses',
        mandatory:   false,
    },
    {
        id:          4,
        dismissKey:  'cogs',
        title:       'Add COGS & Tax',
        label:       'Step 04',
        icon:        '🧾',
        description: 'Enter your cost of goods sold and tax rates for each product to enable accurate margin calculations.',
        ctaLabel:    'Add COGS & Tax',
        ctaLink:     '/settings/products',
        mandatory:   false,
    },
];

// ─── Helper: completion status per step ──────────────────────────────────────

const getCompletionStatus = (userData) => ({
    1: userData?.user?.user_data?.is_amz_sp_api_connected       === 1,
    2: userData?.user?.user_data?.is_amz_ads_connected          === 1,
    3: userData?.user?.user_data?.is_expense_data_added         === true,
    4: userData?.user?.user_data?.is_product_cogs_n_tax_added   === true,
});

// ─── Sub-component: Step indicator pill ──────────────────────────────────────

const StepPill = ({ step, isCurrent, isCompleted, isDismissed, isLocked, onClick }) => {
    const pill = (
        <button
            onClick={isLocked ? undefined : onClick}
            style={{
                display:        'flex',
                alignItems:     'center',
                gap:            8,
                padding:        '8px 16px',
                borderRadius:   100,
                border:         isCurrent
                    ? '2px solid #dc3545'
                    : isCompleted || isDismissed
                        ? '2px solid #52c41a'
                        : '2px solid #e0e0e0',
                background:     isCurrent
                    ? '#fff0f1'
                    : isCompleted || isDismissed
                        ? '#f6ffed'
                        : '#fafafa',
                cursor:         isLocked ? 'not-allowed' : 'pointer',
                opacity:        isLocked ? 0.45 : 1,
                transition:     'all 0.2s ease',
                whiteSpace:     'nowrap',
                fontFamily:     'inherit',
                outline:        'none',
            }}
        >
            {/* Status icon */}
            {isCompleted ? (
                <CheckCircleFilled style={{ color: '#52c41a', fontSize: 15 }} />
            ) : isDismissed ? (
                <CloseOutlined style={{ color: '#52c41a', fontSize: 12 }} />
            ) : (
                <span
                    style={{
                        width:        20,
                        height:       20,
                        borderRadius: '50%',
                        background:   isCurrent ? '#dc3545' : '#d9d9d9',
                        display:      'flex',
                        alignItems:   'center',
                        justifyContent: 'center',
                        fontSize:     10,
                        color:        '#fff',
                        fontWeight:   700,
                        flexShrink:   0,
                    }}
                >
                    {step.id}
                </span>
            )}

            <span
                style={{
                    fontSize:   12,
                    fontWeight: isCurrent ? 700 : 500,
                    color:      isCurrent
                        ? '#dc3545'
                        : isCompleted || isDismissed
                            ? '#389e0d'
                            : '#555',
                }}
            >
                {step.title}
            </span>
        </button>
    );

    if (isLocked) {
        return (
            <Tooltip title="Please connect SP API first" placement="bottom">
                {pill}
            </Tooltip>
        );
    }

    return pill;
};

// ─── Sub-component: Progress bar ─────────────────────────────────────────────

const ProgressBar = ({ completedCount, total }) => (
    <div
        style={{
            height:       4,
            background:   '#f0f0f0',
            borderRadius: 2,
            overflow:     'hidden',
            margin:       '0 0 20px',
        }}
    >
        <div
            style={{
                height:     '100%',
                width:      `${(completedCount / total) * 100}%`,
                background: 'linear-gradient(90deg, #f45b68, #dc3545)',
                borderRadius: 2,
                transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
        />
    </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * SetupStepsBanner
 *
 * Fully isolated onboarding carousel banner.
 * Drop it above all other content on any page — it has no side effects on
 * existing functionality.
 *
 * @param {object} userData  — same userData prop used throughout the app
 */
const SetupStepsBanner = ({ userData }) => {

    // ── Completion & dismiss state ────────────────────────────────────────────
    const completed  = useMemo(() => getCompletionStatus(userData), [userData]);
    const [dismissed, setDismissed] = useState(() => readDismissed());
    const [currentStep, setCurrentStep] = useState(1); // 1-indexed

    const step1Done = completed[1];

    // ── Determine which steps are still "visible" (not done AND not dismissed) ─
    const visibleSteps = useMemo(() =>
            STEPS.filter(s => {
                const done = completed[s.id];
                const dis  = s.dismissKey ? dismissed[s.dismissKey] : false;
                return !done && !dis;
            }),
        [completed, dismissed]);

    // ── Hide banner when all steps are satisfied ──────────────────────────────
    const allDone = useMemo(() => {
        return (
            completed[1] &&
            (completed[2] || dismissed.ads)     &&
            (completed[3] || dismissed.expense) &&
            (completed[4] || dismissed.cogs)
        );
    }, [completed, dismissed]);

    // Auto-advance to first incomplete step when data changes
    useEffect(() => {
        if (!step1Done) { setCurrentStep(1); return; }
        const first = visibleSteps[0];
        if (first && !visibleSteps.find(s => s.id === currentStep)) {
            setCurrentStep(first.id);
        }
    }, [completed, dismissed]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Dismiss handler ───────────────────────────────────────────────────────
    const handleDismiss = useCallback((dismissKey) => {
        localStorage.setItem(LS_KEYS[dismissKey], 'true');
        setDismissed(prev => ({ ...prev, [dismissKey]: true }));

        // Move to next visible step after dismissing current
        const remaining = visibleSteps.filter(s => s.dismissKey !== dismissKey);
        if (remaining.length > 0) setCurrentStep(remaining[0].id);
    }, [visibleSteps]);

    // ── Navigation ────────────────────────────────────────────────────────────
    const navigablePills = STEPS; // all 4 always shown in the pill bar

    const handlePrev = () => {
        const idx = STEPS.findIndex(s => s.id === currentStep);
        if (idx > 0) setCurrentStep(STEPS[idx - 1].id);
    };

    const handleNext = () => {
        const idx = STEPS.findIndex(s => s.id === currentStep);
        if (idx < STEPS.length - 1) setCurrentStep(STEPS[idx + 1].id);
    };

    const canGoPrev = STEPS.findIndex(s => s.id === currentStep) > 0;
    const canGoNext = STEPS.findIndex(s => s.id === currentStep) < STEPS.length - 1;

    // ── Derived values for current step card ─────────────────────────────────
    const activeStepDef  = STEPS.find(s => s.id === currentStep);
    const isCurrentDone  = completed[currentStep];
    const isCurrentDis   = activeStepDef?.dismissKey
        ? dismissed[activeStepDef.dismissKey]
        : false;
    const isCurrentLocked = !step1Done && currentStep > 1;

    // Completed count for progress bar (done OR dismissed counts as "handled")
    const handledCount = STEPS.filter(s => {
        const done = completed[s.id];
        const dis  = s.dismissKey ? dismissed[s.dismissKey] : false;
        return done || dis;
    }).length;

    // ── Don't render if everything is done ───────────────────────────────────
    if (allDone) return null;

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <div
            style={{
                width:        '100%',
                background:   '#fff',
                borderRadius: 12,
                boxShadow:    '0 2px 16px rgba(0,0,0,0.08)',
                border:       '1px solid #f0f0f0',
                marginBottom: 24,
                overflow:     'hidden',
            }}
        >
            {/* ── Header bar ── */}
            <div
                style={{
                    background:     'linear-gradient(135deg, #1e1e2d 0%, #2d2d44 100%)',
                    padding:        '14px 24px',
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'space-between',
                    gap:            12,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 18 }}>🚀</span>
                    <div>
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>
                            Complete Your Setup
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, marginTop: 2 }}>
                            {handledCount} of {STEPS.length} steps completed
                        </div>
                    </div>
                </div>

                {/* Step pills — scrollable on small screens */}
                <div
                    style={{
                        display:    'flex',
                        gap:        8,
                        overflowX:  'auto',
                        flexShrink: 0,
                        maxWidth:   '70%',
                        paddingBottom: 2,
                    }}
                >
                    {navigablePills.map(step => (
                        <StepPill
                            key={step.id}
                            step={step}
                            isCurrent={currentStep === step.id}
                            isCompleted={completed[step.id]}
                            isDismissed={step.dismissKey ? dismissed[step.dismissKey] : false}
                            isLocked={!step1Done && step.id > 1}
                            onClick={() => setCurrentStep(step.id)}
                        />
                    ))}
                </div>
            </div>

            {/* ── Progress bar ── */}
            <div style={{ padding: '16px 24px 0' }}>
                <ProgressBar completedCount={handledCount} total={STEPS.length} />
            </div>

            {/* ── Step card ── */}
            <div
                key={currentStep} // re-mount for fade-in on step change
                style={{
                    padding:   '0 24px 20px',
                    animation: 'sbFadeIn 0.25s ease',
                }}
            >
                <style>{`
                    @keyframes sbFadeIn {
                        from { opacity: 0; transform: translateY(6px); }
                        to   { opacity: 1; transform: translateY(0);   }
                    }
                `}</style>

                <div
                    style={{
                        display:       'flex',
                        alignItems:    'flex-start',
                        gap:           20,
                        background:    isCurrentDone || isCurrentDis
                            ? '#f6ffed'
                            : isCurrentLocked
                                ? '#fafafa'
                                : '#fff9f9',
                        border:        `1px solid ${
                            isCurrentDone || isCurrentDis
                                ? '#b7eb8f'
                                : isCurrentLocked
                                    ? '#e8e8e8'
                                    : '#ffd6d9'
                        }`,
                        borderRadius:  10,
                        padding:       '20px 24px',
                    }}
                >
                    {/* Icon */}
                    <div
                        style={{
                            fontSize:    32,
                            lineHeight:  1,
                            flexShrink:  0,
                            opacity:     isCurrentLocked ? 0.35 : 1,
                        }}
                    >
                        {isCurrentDone
                            ? '✅'
                            : isCurrentDis
                                ? '⏭️'
                                : activeStepDef?.icon}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Label */}
                        <div
                            style={{
                                fontSize:    11,
                                fontWeight:  700,
                                color:       '#dc3545',
                                textTransform: 'uppercase',
                                letterSpacing: '0.6px',
                                marginBottom: 4,
                            }}
                        >
                            {activeStepDef?.label}
                            {activeStepDef?.mandatory && (
                                <span
                                    style={{
                                        marginLeft:   8,
                                        background:   '#dc3545',
                                        color:        '#fff',
                                        fontSize:     9,
                                        padding:      '1px 6px',
                                        borderRadius: 3,
                                        fontWeight:   700,
                                        letterSpacing: '0.3px',
                                    }}
                                >
                                    MANDATORY
                                </span>
                            )}
                        </div>

                        {/* Title */}
                        <div
                            style={{
                                fontSize:   17,
                                fontWeight: 700,
                                color:      isCurrentLocked ? '#aaa' : '#1e1e2d',
                                marginBottom: 6,
                            }}
                        >
                            {activeStepDef?.title}
                        </div>

                        {/* Description */}
                        <p
                            style={{
                                fontSize:     13,
                                color:        isCurrentLocked ? '#bbb' : '#666',
                                margin:       '0 0 16px',
                                lineHeight:   1.6,
                                maxWidth:     520,
                            }}
                        >
                            {isCurrentDone
                                ? '✓ This step is complete. You\'re all set here!'
                                : isCurrentDis
                                    ? 'You\'ve chosen to skip this step. You can always revisit it later from Settings.'
                                    : activeStepDef?.description}
                        </p>

                        {/* CTAs */}
                        {!isCurrentDone && !isCurrentDis && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>

                                {/* Primary CTA */}
                                {isCurrentLocked ? (
                                    <Tooltip title="Please connect SP API first">
                                        <span
                                            style={{
                                                display:       'inline-flex',
                                                alignItems:    'center',
                                                gap:           6,
                                                padding:       '9px 20px',
                                                borderRadius:  6,
                                                background:    '#f5f5f5',
                                                color:         '#aaa',
                                                fontSize:      13,
                                                fontWeight:    600,
                                                cursor:        'not-allowed',
                                                border:        '1px solid #e8e8e8',
                                                userSelect:    'none',
                                            }}
                                        >
                                            {activeStepDef?.ctaLabel}
                                            <ArrowRightOutlined style={{ fontSize: 11 }} />
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Link
                                        to={activeStepDef?.ctaLink}
                                        style={{
                                            display:       'inline-flex',
                                            alignItems:    'center',
                                            gap:           6,
                                            padding:       '9px 20px',
                                            borderRadius:  6,
                                            background:    '#dc3545',
                                            color:         '#fff',
                                            fontSize:      13,
                                            fontWeight:    600,
                                            textDecoration: 'none',
                                            boxShadow:     '0 2px 8px rgba(220,53,69,0.3)',
                                            transition:    'opacity 0.15s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                    >
                                        {activeStepDef?.ctaLabel}
                                        <ArrowRightOutlined style={{ fontSize: 11 }} />
                                    </Link>
                                )}

                                {/* Don't remind me — only for non-mandatory, non-locked steps */}
                                {!activeStepDef?.mandatory && !isCurrentLocked && (
                                    <button
                                        onClick={() => handleDismiss(activeStepDef.dismissKey)}
                                        style={{
                                            background:    'none',
                                            border:        'none',
                                            padding:       0,
                                            fontSize:      12,
                                            color:         '#aaa',
                                            cursor:        'pointer',
                                            textDecoration: 'underline',
                                            fontFamily:    'inherit',
                                        }}
                                    >
                                        Don't remind me again
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Status badge — top-right of card */}
                    {(isCurrentDone || isCurrentDis) && (
                        <div
                            style={{
                                flexShrink:    0,
                                background:    isCurrentDone ? '#52c41a' : '#8c8c8c',
                                color:         '#fff',
                                fontSize:      10,
                                fontWeight:    700,
                                padding:       '3px 10px',
                                borderRadius:  20,
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                alignSelf:     'flex-start',
                                marginTop:     2,
                            }}
                        >
                            {isCurrentDone ? 'Done' : 'Skipped'}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Footer navigation ── */}
            <div
                style={{
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'space-between',
                    padding:        '10px 24px 16px',
                    borderTop:      '1px solid #f5f5f5',
                }}
            >
                {/* Step dots */}
                <div style={{ display: 'flex', gap: 6 }}>
                    {STEPS.map(s => (
                        <button
                            key={s.id}
                            onClick={() => (!step1Done && s.id > 1) ? undefined : setCurrentStep(s.id)}
                            style={{
                                width:        s.id === currentStep ? 20 : 8,
                                height:       8,
                                borderRadius: 4,
                                border:       'none',
                                padding:      0,
                                cursor:       (!step1Done && s.id > 1) ? 'not-allowed' : 'pointer',
                                background:   s.id === currentStep
                                    ? '#dc3545'
                                    : completed[s.id] || (s.dismissKey && dismissed[s.dismissKey])
                                        ? '#52c41a'
                                        : '#e0e0e0',
                                transition:   'all 0.25s ease',
                                opacity:      (!step1Done && s.id > 1) ? 0.4 : 1,
                            }}
                        />
                    ))}
                </div>

                {/* Prev / Next */}
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        onClick={handlePrev}
                        disabled={!canGoPrev}
                        style={{
                            display:      'inline-flex',
                            alignItems:   'center',
                            gap:          6,
                            padding:      '6px 14px',
                            borderRadius: 6,
                            border:       '1px solid #e0e0e0',
                            background:   '#fafafa',
                            fontSize:     12,
                            fontWeight:   500,
                            color:        canGoPrev ? '#333' : '#ccc',
                            cursor:       canGoPrev ? 'pointer' : 'not-allowed',
                            fontFamily:   'inherit',
                            transition:   'background 0.15s',
                        }}
                    >
                        <ArrowLeftOutlined style={{ fontSize: 10 }} /> Previous
                    </button>
                    <button
                        onClick={handleNext}
                        disabled={!canGoNext}
                        style={{
                            display:      'inline-flex',
                            alignItems:   'center',
                            gap:          6,
                            padding:      '6px 14px',
                            borderRadius: 6,
                            border:       '1px solid #e0e0e0',
                            background:   canGoNext ? '#1e1e2d' : '#fafafa',
                            fontSize:     12,
                            fontWeight:   500,
                            color:        canGoNext ? '#fff' : '#ccc',
                            cursor:       canGoNext ? 'pointer' : 'not-allowed',
                            fontFamily:   'inherit',
                            transition:   'background 0.15s',
                        }}
                    >
                        Next <ArrowRightOutlined style={{ fontSize: 10 }} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SetupStepsBanner;